import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { extractErrorMessage } from "@/api/client";
import type { ListingAgentDto } from "@/api/buyers";
import {
  AGENT_RESEARCH_ERROR,
  agentResearchErrorDetails,
  listingAgentResearchApi,
  type ListingAgentResearchRunDto,
  type ListingAgentResearchSavePayload,
  type ListingAgentResearchSavePreviewDto,
} from "@/api/listingAgentResearch";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { Button, Skeleton, Timestamp, useToast } from "@/design-system/components/ui";
import { buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";
import { listingAgentResearchKeys } from "@/hooks/buyers/listingAgentResearchKeys";
import { DetailField, DetailFieldList, DetailSection } from "../../detailBits";
import { AgentAlreadyOnFilePanel } from "../../ListingAgentDirectory";
import { BLOCKER_TEXT, agentResearchCopy } from "./agentResearchCopy";
import { SafeLink } from "./StartStage";

type AgentChoice = { kind: "create" } | { kind: "existing"; agent: ListingAgentDto };

/** What one SAVE will write, as the confirmation states it. */
export function saveSummary(
  preview: ListingAgentResearchSavePreviewDto,
  choice: AgentChoice | null,
  fill: string[],
  applyListingUrl: boolean,
  applyContactFormUrl: boolean,
  addNotesLine = false,
): string[] {
  const lines: string[] = [];
  const proposed = preview.proposedAgent;
  if (preview.mode === "CreateAgent" && choice?.kind === "create" && proposed) {
    const name = proposed.isFirmContact
      ? `a firm contact for ${proposed.firmName ?? "the firm"}`
      : [proposed.firstName, proposed.lastName].filter(Boolean).join(" ") || "the agent";
    lines.push(`Create ${name} and attach them to this listing.`);
  }
  if (choice?.kind === "existing") {
    lines.push(`Attach ${choice.agent.displayName}, already on file, to this listing.`);
  }
  if (preview.mode === "FillAgent" && fill.length > 0 && preview.attachedAgent) {
    const labels = preview.fillPlan.filter((p) => fill.includes(p.field)).map((p) => p.fieldLabel.toLowerCase());
    lines.push(`Add ${labels.join(", ")} to ${preview.attachedAgent.displayName}.`);
  }
  if (applyListingUrl && preview.listingUrl) lines.push(`Replace the listing URL with ${preview.listingUrl.newUrl}.`);
  if (applyContactFormUrl && preview.contactFormUrl) lines.push(`Save the contact form ${preview.contactFormUrl.newUrl}.`);
  if (addNotesLine && preview.notesLine && preview.attachedAgent) {
    lines.push(`Add to ${preview.attachedAgent.displayName}'s notes: ${preview.notesLine}`);
  }
  const onFile = preview.alreadyOnFile?.length ?? 0;
  if (onFile > 0) {
    lines.push(`Mark ${onFile === 1 ? "1 finding" : `${onFile} findings`} already on file as saved (nothing is changed).`);
  }
  return lines;
}

/**
 * ⑤ Review & save (D16, D17) — what the accepted findings would write, and ONE
 * confirmed action that writes it. A new agent goes through the duplicate check
 * (USE THIS AGENT / CREATE NEW ANYWAY); an attached agent gets only the blank
 * fields the specialist ticks; the listing URL (R1) and contact form change only
 * when ticked. Listings.Manage only — the preview itself is a Manage read.
 */
export function ReviewStage({
  listingId,
  run,
  canManage,
}: {
  listingId: string;
  run: ListingAgentResearchRunDto | null | undefined;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const preview = useQuery({
    queryKey: listingAgentResearchKeys.savePreview(listingId, run?.id ?? ""),
    queryFn: () => listingAgentResearchApi.savePreview(listingId, run!.id),
    enabled: Boolean(canManage && run && !run.isActive),
  });

  const [choice, setChoice] = useState<AgentChoice | null>(null);
  const [fill, setFill] = useState<string[]>([]);
  const [applyListingUrl, setApplyListingUrl] = useState(false);
  const [applyContactFormUrl, setApplyContactFormUrl] = useState(false);
  const [addNotesLine, setAddNotesLine] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState<{ text: string; otherListingId?: string; existingAgentId?: string } | null>(null);

  const p = preview.data;
  // A fresh preview resets the choices: they belong to what it proposes.
  useEffect(() => {
    if (!p) return;
    const matches = p.duplicateCheck?.matches ?? [];
    setChoice(p.mode === "CreateAgent" && matches.length === 0 ? { kind: "create" } : null);
    setFill([]);
    setApplyListingUrl(false);
    setApplyContactFormUrl(false);
    setAddNotesLine(false);
  }, [p]);

  const save = useMutation({
    mutationFn: (body: ListingAgentResearchSavePayload) => listingAgentResearchApi.save(listingId, run!.id, body),
    onSuccess: () => {
      setProblem(null);
      // listingKeys.all also covers every research key (they nest under it).
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(agentResearchCopy.savedToast);
    },
    onError: (e) => {
      const d = agentResearchErrorDetails(e);
      if (d?.code === AGENT_RESEARCH_ERROR.listingAgentAlreadyOnFile) {
        setProblem({
          text: "An agent with these details is already on file. Use that agent instead.",
          existingAgentId: d.existingAgentId,
        });
        void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.savePreview(listingId, run!.id) });
      } else if (d?.code === AGENT_RESEARCH_ERROR.listingUrlCollision) {
        setProblem({
          text: `That URL already belongs to another listing${d.otherListingName ? `: ${d.otherListingName}` : ""}.`,
          otherListingId: d.otherListingId,
        });
      } else if (d?.code === AGENT_RESEARCH_ERROR.researchRunActive) {
        setProblem({ text: agentResearchCopy.waitForRun });
      } else if (d?.code === AGENT_RESEARCH_ERROR.researchAlreadySaved) {
        setProblem({ text: BLOCKER_TEXT.researchAlreadySaved });
      } else {
        setProblem({ text: extractErrorMessage(e) });
      }
    },
  });

  const summary = useMemo(
    () => (p ? saveSummary(p, choice, fill, applyListingUrl, applyContactFormUrl, addNotesLine) : []),
    [p, choice, fill, applyListingUrl, applyContactFormUrl, addNotesLine],
  );

  if (!run) return <p className="text-small text-gray-mid m-0">{agentResearchCopy.noRunYet}</p>;

  const saved = run.save ? <SavedRecord run={run} /> : null;

  if (!canManage) {
    return saved ?? <p className="text-small text-gray-mid m-0">Nothing from this run has been saved.</p>;
  }
  if (run.isActive) {
    return (
      <p className="text-small text-gray-mid m-0" data-testid="agent-research-review-wait">
        {agentResearchCopy.waitForRun}
      </p>
    );
  }
  if (preview.isLoading) return <Skeleton className="h-space-6 w-full" />;
  if (preview.isError || !p) {
    return (
      <p className="text-small text-status-error m-0" role="alert">
        {extractErrorMessage(preview.error)}
      </p>
    );
  }

  const blockers = p.blockers.filter((b) => b !== "nothingToSave");
  const onFile = p.alreadyOnFile ?? [];
  const nothing =
    p.mode === "None" && !p.listingUrl && !p.contactFormUrl && !p.notesLine && onFile.length === 0;

  const body: ListingAgentResearchSavePayload = {
    createAgent: choice?.kind === "create",
    useExistingAgentId: choice?.kind === "existing" ? choice.agent.id : null,
    fillFields: fill,
    applyListingUrl,
    applyContactFormUrl,
    addNotesLine,
  };

  return (
    <div className="grid gap-space-5" data-testid="agent-research-review">
      {saved}

      {blockers.map((b) => (
        <p key={b} className="text-small text-status-warning-ink m-0" data-testid={`agent-research-blocker-${b}`}>
          {BLOCKER_TEXT[b] ?? b}
        </p>
      ))}

      {nothing && (
        <p className="text-small text-gray-mid m-0" data-testid="agent-research-nothing-to-save">
          {agentResearchCopy.nothingToSave}
        </p>
      )}

      {p.mode === "CreateAgent" && p.proposedAgent && (
        <DetailSection title="New agent">
          <DetailFieldList>
            {p.proposedAgent.isFirmContact ? (
              <DetailField label="Contact" value="Firm contact (no named agent)" />
            ) : (
              <DetailField
                label="Name"
                value={[p.proposedAgent.firstName, p.proposedAgent.lastName].filter(Boolean).join(" ") || null}
              />
            )}
            <DetailField label="Firm" value={p.proposedAgent.firmName} />
            <DetailField label="Title" value={p.proposedAgent.title} />
            <DetailField label="Email" value={p.proposedAgent.email} />
            <DetailField label="Phone" value={p.proposedAgent.phone} />
            <DetailField label="City / state" value={[p.proposedAgent.city, p.proposedAgent.state].filter(Boolean).join(", ") || null} />
            {p.proposedAgent.notesLine && <DetailField label="Notes" wide value={p.proposedAgent.notesLine} />}
          </DetailFieldList>
          {p.duplicateCheck && p.duplicateCheck.matches.length > 0 && (
            <div className="mt-space-3">
              <AgentAlreadyOnFilePanel
                check={p.duplicateCheck}
                raced={false}
                disabled={save.isPending}
                onUseExisting={(agent) => setChoice({ kind: "existing", agent })}
                onCreateNew={() => setChoice({ kind: "create" })}
              />
              {choice && (
                <p className="text-caption text-onyx mt-space-2 m-0" data-testid="agent-research-agent-choice">
                  {choice.kind === "existing"
                    ? `Will attach ${choice.agent.displayName}.`
                    : "Will create a new agent anyway."}
                </p>
              )}
            </div>
          )}
        </DetailSection>
      )}

      {p.mode === "FillAgent" && p.attachedAgent && (
        <DetailSection title={`Add to ${p.attachedAgent.displayName}`}>
          <ul className="m-0 grid list-none gap-space-2 p-0" data-testid="agent-research-fill-plan">
            {p.fillPlan.map((item) => (
              <li key={item.field} className="grid gap-space-1">
                <label className="flex items-start gap-space-2 text-small">
                  <input
                    type="checkbox"
                    disabled={!item.fillable || save.isPending}
                    checked={fill.includes(item.field)}
                    onChange={(e) =>
                      setFill((prev) => (e.target.checked ? [...prev, item.field] : prev.filter((f) => f !== item.field)))
                    }
                    data-testid={`agent-research-fill-${item.field}`}
                  />
                  <span>
                    <span className="font-medium">{item.fieldLabel}:</span> {item.value}
                    {item.currentValue && <span className="text-gray-mid"> (on file: {item.currentValue})</span>}
                  </span>
                </label>
                {!item.fillable && item.reason && <span className="text-caption text-gray-mid pl-space-5">{item.reason}</span>}
              </li>
            ))}
          </ul>
        </DetailSection>
      )}

      {p.notesLine && p.attachedAgent && (
        <DetailSection title="Notes line">
          <div className="grid gap-space-2" data-testid="agent-research-notes-line">
            <p className="text-small text-gray-mid m-0">
              These accepted findings have no field to fill on {p.attachedAgent.displayName}:
            </p>
            <p className="text-small text-onyx m-0 break-words">{p.notesLine}</p>
            <label className="flex items-center gap-space-2 text-small">
              <input
                type="checkbox"
                checked={addNotesLine}
                disabled={save.isPending}
                onChange={(e) => setAddNotesLine(e.target.checked)}
                data-testid="agent-research-notes-line-apply"
              />
              Add this line to the agent&apos;s notes
            </label>
          </div>
        </DetailSection>
      )}

      {onFile.length > 0 && (
        <DetailSection title="Already on file">
          <ul className="m-0 grid list-none gap-space-1 p-0" data-testid="agent-research-already-on-file">
            {onFile.map((f) => (
              <li key={f.findingId} className="text-small">
                <span className="font-medium">{f.fieldLabel}:</span> <span className="break-all">{f.value}</span>
              </li>
            ))}
          </ul>
          <p className="text-caption text-gray-mid mt-space-2 m-0">Saving marks these saved; nothing about them changes.</p>
        </DetailSection>
      )}

      {p.listingUrl && (
        <UrlChange
          title="Listing URL"
          testId="agent-research-listing-url"
          change={p.listingUrl}
          checked={applyListingUrl}
          onChange={setApplyListingUrl}
          disabled={save.isPending}
          label="Replace the listing URL"
        />
      )}
      {p.contactFormUrl && (
        <UrlChange
          title="Contact form"
          testId="agent-research-contact-form"
          change={p.contactFormUrl}
          checked={applyContactFormUrl}
          onChange={setApplyContactFormUrl}
          disabled={save.isPending}
          label="Save the contact form to the listing"
        />
      )}

      {problem && (
        <div className="grid gap-space-2" role="alert" data-testid="agent-research-save-error">
          <p className="text-small text-status-error m-0">{problem.text}</p>
          {problem.existingAgentId && (
            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({ ...body, createAgent: false, useExistingAgentId: problem.existingAgentId })
                }
                data-testid="agent-research-use-existing"
              >
                Use the agent on file
              </Button>
            </div>
          )}
          {problem.otherListingId && (
            <Link to={`/listings/${problem.otherListingId}`} className="text-small text-gold-shadow hover:underline">
              Open that listing
            </Link>
          )}
        </div>
      )}

      {!nothing && (
        <div>
          <Button
            onClick={() => setConfirming(true)}
            disabled={save.isPending || blockers.length > 0 || summary.length === 0}
            data-testid="agent-research-save"
          >
            Save
          </Button>
        </div>
      )}

      <ConfirmModal
        open={confirming}
        title="Save to the listing?"
        message={
          <ul className="m-0 grid gap-space-1 pl-space-4" data-testid="agent-research-save-summary">
            {summary.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        }
        confirmLabel="Save"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          save.mutate(body);
        }}
      />
    </div>
  );
}

function UrlChange({
  title,
  testId,
  change,
  checked,
  onChange,
  disabled,
  label,
}: {
  title: string;
  testId: string;
  change: NonNullable<ListingAgentResearchSavePreviewDto["listingUrl"]>;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <DetailSection title={title}>
      <div className="grid gap-space-2" data-testid={testId}>
        <p className="text-small m-0">
          New: <SafeLink url={change.newUrl} />
        </p>
        <p className="text-small text-gray-mid m-0">Now: {change.currentUrl ? <SafeLink url={change.currentUrl} /> : "none on file"}</p>
        {change.allowed ? (
          <label className="flex items-center gap-space-2 text-small">
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked)}
              data-testid={`${testId}-apply`}
            />
            {label}
          </label>
        ) : (
          <p className="text-small text-status-warning-ink m-0" data-testid={`${testId}-blocked`}>
            {change.reason ?? "This change cannot be saved."}
            {change.collidesWithListingId && (
              <>
                {" "}
                <Link to={`/listings/${change.collidesWithListingId}`} className="text-gold-shadow hover:underline">
                  {change.collidesWithListingName ?? "Open that listing"}
                </Link>
              </>
            )}
          </p>
        )}
      </div>
    </DetailSection>
  );
}

function SavedRecord({ run }: { run: ListingAgentResearchRunDto }) {
  const s = run.save!;
  return (
    <DetailSection title="Saved">
      <div className="grid gap-space-1" data-testid="agent-research-saved">
        <p className="text-small text-onyx m-0">
          Saved <Timestamp value={s.savedAtUtc} />.
        </p>
        {s.listingUrlReplacedTo && (
          <p className="text-small m-0">
            Listing URL replaced: {s.listingUrlReplacedFrom ?? "none"} → <SafeLink url={s.listingUrlReplacedTo} />
          </p>
        )}
        {s.contactFormUrlSaved && (
          <p className="text-small m-0">
            Contact form saved: <SafeLink url={s.contactFormUrlSaved} />
          </p>
        )}
      </div>
    </DetailSection>
  );
}
